import { Box, Breadcrumbs, Paper, Stack } from "@bvhi/ui-base";
import { NavigateNextIcon } from "@bvhi/ui-icons";
import { useMatches, useParams } from "@tanstack/react-router";
import { ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BVHIRoute, getConfigForRouteByRoutId } from "../config/navigation";
import { RouterLink } from "./router-link";

interface Props {
  showBreadcrumbs: boolean;
  labelOverride?: (route: BVHIRoute) => string | null;
  actions?: ReactNode[];
}

export const NavigationBar = ({ showBreadcrumbs, actions, labelOverride }: Props) => {
  const { catalogId, catalogVersionId, audiologistId, posId, userId, verificationCodeId } = useParams({ strict: false });
  const { t } = useTranslation(["routes"]);
  const matches = useMatches();

  const breadcrumbsPathes = useMemo(() => {
    // has duplicates, since pathname includes layout components which just use root path
    const routesIds = matches.map((match) => {
      return match.routeId;
    });

    // remove duplicates
    return [...new Set(routesIds)];
  }, [matches]);

  const generateBreadcrumbLabel = useCallback(
    (route: BVHIRoute) => {
      if (labelOverride) {
        const overridenLabel = labelOverride(route);
        if (overridenLabel) return overridenLabel;
      }
      if (route.label) return t(route.label);
      return "";
    },
    [labelOverride, t]
  );

  return (
    <>
      <Box
        sx={{
          width: "calc(100% + 20px)",
          height: (theme) => theme.spacing(2),
          position: "sticky",
          transform: "translateX(-10px)",
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          background: (theme) => theme.palette.background.default,
        }}
      />
      <Paper
        aria-label="Breadcrumb"
        sx={{
          position: "sticky",
          minHeight: 36,
          top: (theme) => theme.spacing(2),
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Box padding={1} paddingLeft={2}>
          <Stack alignItems="center" direction="row" minHeight={31}>
            {showBreadcrumbs && (
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<NavigateNextIcon fontSize="small" />}
                sx={{ flexGrow: 1, minHeight: "100%", height: "100%" }}
              >
                {breadcrumbsPathes.map((path) => {
                  const config = getConfigForRouteByRoutId(path);

                  if (!config) return undefined;
                  if (!config.shouldAppearInNavigationBar) return undefined;

                  return (
                    <RouterLink
                      key={path}
                      color="inherit"
                      sx={{ display: "flex", alignItems: "center", userSelect: "none" }}
                      to={config.path}
                      underline="hover"
                      params={{
                        ...(catalogId && { catalogId }),
                        ...(catalogVersionId && { catalogVersionId }),
                        ...(audiologistId && { audiologistId }),
                        ...(posId && { posId }),
                        ...(userId && { userId }),
                        ...(verificationCodeId && { verificationCodeId }),
                      }}
                    >
                      {config.icon && <config.icon fontSize="inherit" sx={{ mr: 0.5 }} />}
                      {generateBreadcrumbLabel(config)}
                    </RouterLink>
                  );
                })}
              </Breadcrumbs>
            )}
            {actions && (
              <Stack direction="row" spacing={2}>
                {actions}
              </Stack>
            )}
          </Stack>
        </Box>
      </Paper>
    </>
  );
};
